import { useInputOrParseUrl } from '@/components/RelationActionMenu/hooks/useInputOrParseUrl';
import { useAgentApplicantResourceHeaders } from '@/context/AgentApplicantOfficeMember';
import { useDownloadS3SignedUrlSuspense } from '@/context/services/downloads/index.service';
import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import { useOperationStatusToErrorMessage } from '@/context/services/invoice/hooks';
import { useGetLazyMfFile } from '@/context/services/mf_file/MfFile.service';
import {
  normalizeResponseError,
  normalizeThrownError,
} from '@/context/services/normalizeError';
import { useRefetchQueries } from '@/context/services/refetch';
import {
  isAuthError,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';
import { parseFileName } from '@/context/services/utils/contentDisposition';
import { extractCommonErrorFromBlob } from '@/context/services/utils/extractCommonErrorFromBlob';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import type { CsvEncodeParameter } from 'ap-openapi';
import {
  BulkUploadReceivedInvoicesErrorResponse,
  BulkUploadReceivedInvoicesRequest,
  BulkUploadReceivedInvoicesResult,
  CommonError,
  getGetReceivedInvoiceQueryKey,
  getSearchReceivedInvoicesQueryKey,
  ReceivedInvoiceDetail,
  ReceivedInvoiceReconciliationStatus,
  RequestReceivedInvoiceTransactionOcrMutationError,
  SearchReceivedInvoicesResponseReceivedInvoicesItem,
  UpdateReceivedInvoiceErrorResponse,
  useArchiveReceivedInvoice,
  useUpdateReceivedInvoice as useBaseUpdateReceivedInvoice,
  useBulkUploadReceivedInvoices,
  useGetReceivedInvoice,
  useGetReceivedInvoiceHook,
  useGetReceivedInvoiceSuspense,
  useGetReceivedInvoiceTransactionOcrCsvHook,
  useGetSignedXmlFileUrlSuspense,
  useReceivedInvoicesSettings,
  useRequestReceivedInvoiceTransactionOcr,
  useRestoreReceivedInvoice,
  useSearchReceivedInvoices,
  useSearchReceivedInvoicesSuspense,
  useUpdateReceivedInvoiceReconciliation,
} from 'ap-openapi';
import { AxiosResponse, isAxiosError } from 'axios';
import { useCallback, useMemo } from 'react';
import type {
  ExistsInvoiceResult,
  FetchReceivedInvoiceHooks,
  FetchReceivedInvoicesArgs,
  FetchReceivedInvoicesModernResult,
  FetchReceivedInvoicesSearchWithReportableAndWithIncludeMfFileResult,
  InvoiceColumn,
  InvoiceColumnWithMfFile,
  ReceivedInvoicesSearchRefetch,
} from './type';

export const useReconciliationServiceIntegrationEnabled = (): boolean => {
  const { data } = useReceivedInvoicesSettings({
    // The invoice list mounts InvoiceTable twice — once as the Suspense
    // fallback, then again once InnerList resolves — so `staleTime: 0` would
    // refetch the settings on the second mount and hit the endpoint twice per
    // page load. The office setting changes rarely, so keep the window.
    query: { staleTime: 60_000 },
  });

  return data?.data?.reconciliation_service_integration?.is_enabled ?? false;
};

const responseToInvoices = <T extends InvoiceColumn>(
  item:
    | SearchReceivedInvoicesResponseReceivedInvoicesItem
    | ReceivedInvoiceDetail
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    id: String(item.searchable_number) ?? '',
    fileName: item.filename,
    payeeCode: item.ap_payee?.code,
    payeeName: item.ap_payee?.name,
    dueDate: item?.due_date,
    totalAmount: item?.total_amount,
    pics:
      item.pics?.map((pic) => ({
        id: pic.id,
        name: pic.display_name || pic.name || '',
      })) ?? [],
    uploadedAt: item.uploaded_at,
    uploadType: item.upload_type,
    operationStatus: item.operation_status,
    aiSuggestStatus: item.ai_suggest_status ?? null,
    automaticProcessingStatus:
      'automatic_processing_status' in item
        ? item.automatic_processing_status ?? null
        : null,
    memo: item.memo ?? '',
    searchId: item.id ?? '',
    mfFile: {
      id: item.mf_file?.id ?? '',
      name: item.mf_file?.name ?? '',
      contentType: item.mf_file?.content_type,
      contentByteSize: item.mf_file?.byte_size,
      eDocMetaDatum: item.mf_file?.e_doc_meta_datum,
    },
    timestampStatus: item.timestamp_status,
    scannerSaveRequirementSatisfied: item.scanner_save_requirement_satisfied,
    receiptType: item.receipt_type,
  } as unknown as T;
};

type FetchPrivateReceivedInvoicesModernResult = {
  pagination: ModernPaginationProps;
  data: SearchReceivedInvoicesResponseReceivedInvoicesItem[] | undefined;
  refetch: ReceivedInvoicesSearchRefetch;
  error: CommonError | null;
  isLoading: boolean;
};

// The custom axios client resolves rather than throws for <500 statuses (see
// ap-openapi's customClientException), so a 401/403 here never reaches an
// error boundary on its own — same recovery as hooks.ts's mutation-error
// handling: reload and let Rails' own middleware redirect + flash.
const reloadOnAuthOrForbiddenError = (status?: number) => {
  if (isAuthError(status) || isForbiddenError(status)) {
    location.reload();
  }
};

const usePrivateReceivedInvoicesSearchWithSuspense = (
  args: FetchReceivedInvoicesArgs = {}
): FetchPrivateReceivedInvoicesModernResult => {
  const headers = useAgentApplicantResourceHeaders('received_invoices');
  const { page, perPage = 25, query } = args;
  const searchArgs = useMemo(
    () => ({
      pagination: {
        per_page: perPage,
        page: page,
      },
      query: query,
      request: {
        headers,
      },
    }),
    [page, headers, perPage, query]
  );
  const { data, error, refetch, isLoading } =
    useSearchReceivedInvoicesSuspense(searchArgs);

  reloadOnAuthOrForbiddenError(data?.status);

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data?.pagination
  );

  const result = useMemo(() => {
    return data?.data.received_invoices;
  }, [data]);

  return {
    pagination,
    data: result,
    refetch,
    error,
    isLoading,
  };
};

const usePrivateReceivedInvoicesSearch = (
  args: FetchReceivedInvoicesArgs = {}
): FetchPrivateReceivedInvoicesModernResult => {
  const headers = useAgentApplicantResourceHeaders('received_invoices');
  const { page = 1, perPage = 25, query, onPageChange } = args;

  const { data, error, refetch, isLoading } = useSearchReceivedInvoices(
    {
      pagination: {
        per_page: perPage,
        page: page,
      },
      query: query,
    },
    {
      request: {
        headers,
      },
    }
  );

  reloadOnAuthOrForbiddenError(data?.status);

  const result = useMemo(() => {
    return data?.data.received_invoices;
  }, [data]);

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data?.pagination,
    onPageChange
  );

  return {
    pagination,
    data: result,
    refetch,
    error,
    isLoading,
  };
};

export function useReceivedInvoicesSearch(
  args: FetchReceivedInvoicesArgs = {}
): FetchReceivedInvoicesModernResult {
  const { pagination, refetch, data } =
    usePrivateReceivedInvoicesSearchWithSuspense(args);
  const values: InvoiceColumn[] =
    data?.map((item) => ({
      id: String(item.searchable_number) ?? '',
      fileName: item.filename,
      payeeCode: item.ap_payee?.code,
      payeeName: item.ap_payee?.name,
      dueDate: item?.due_date,
      totalAmount: item?.total_amount,
      pics:
        item.pics?.map((pic) => ({
          id: pic.id,
          name: pic.name || '',
        })) ?? [],
      uploadedAt: item.uploaded_at,
      uploadType: item.upload_type,
      operationStatus: item.operation_status,
      // Absent from the response unless the office has 突合サービス連携 enabled.
      reconciliationStatus: item.reconciliation_status,
      aiSuggestStatus: item.ai_suggest_status ?? null,
      automaticProcessingStatus: item.automatic_processing_status ?? null,
      timestampStatus: item.timestamp_status ?? undefined,
      memo: item.memo ?? '',
      searchId: item.id ?? '',
      uploader: item.uploader ?? undefined,
      errorMessages: item?.error_messages || undefined,
    })) ?? [];
  return {
    pagination,
    data: values,
    refetch,
  };
}

export function useReceivedInvoicesSearchWithReportableAndWithIncludeMfFile(
  args: FetchReceivedInvoicesArgs = {}
): FetchReceivedInvoicesSearchWithReportableAndWithIncludeMfFileResult {
  if (!args.query) {
    args.query = {};
  }
  args.query.reportable = true;
  args.query.with_mf_file = true;
  const { data, pagination, refetch, isLoading, error } =
    usePrivateReceivedInvoicesSearch(args);
  const values = data?.map(responseToInvoices<InvoiceColumnWithMfFile>) ?? [];
  return {
    data: values,
    pagination,
    refetch,
    isLoading,
    isError: Boolean(error),
  };
}

export const useExistsInvoice = (
  invoiceSearchableId: string
): ExistsInvoiceResult => {
  const parseUrl = useInputOrParseUrl('/received_invoices', 'id');
  const headers = useAgentApplicantResourceHeaders('received_invoices');
  const { t } = useTranslation();
  const searchableId = useMemo(() => {
    const v = parseUrl(invoiceSearchableId);
    if (v) {
      const regexp = /([iI][vV])-/;
      return v.replace(regexp, '');
    }
    return '';
  }, [invoiceSearchableId, parseUrl]);
  const options: Parameters<
    typeof useGetReceivedInvoice<
      Awaited<ReturnType<ReturnType<typeof useGetReceivedInvoiceHook>>>
    >
  >[1] = useMemo(
    () => ({
      query: {
        enabled: Boolean(searchableId),
        queryKey: getGetReceivedInvoiceQueryKey(invoiceSearchableId),
      },
      request: {
        headers,
      },
    }),
    [headers, invoiceSearchableId, searchableId]
  );
  const { data, error, isFetching, isRefetching, isLoading } =
    useGetReceivedInvoice(searchableId, options);
  const operationStatusToErrorMessage = useOperationStatusToErrorMessage(
    data?.data?.operation_status
  );
  const mfFileId = useMemo(() => {
    if (operationStatusToErrorMessage) {
      return '';
    }
    return data?.data?.mf_file?.id || '';
  }, [data?.data?.mf_file?.id, operationStatusToErrorMessage]);
  const {
    data: mfFileData,
    error: mfFileError,
    isFetching: mfFileIsFetching,
  } = useGetLazyMfFile(mfFileId);
  return useMemo(() => {
    if (
      !searchableId ||
      [isLoading, isFetching, mfFileIsFetching, isRefetching].some((v) => v)
    ) {
      return undefined;
    }
    if (error || mfFileError) {
      return {
        data: undefined,
        error: t('system_error_input'),
      };
    } else {
      let error: string | undefined = undefined;
      const status = data?.status;
      let resultValue: InvoiceColumnWithMfFile | undefined = undefined;
      switch (status) {
        case undefined:
          break;
        case 200:
          error = operationStatusToErrorMessage;
          if (error === undefined && data?.data) {
            resultValue = responseToInvoices<InvoiceColumnWithMfFile>(
              data?.data
            );
          }
          if (resultValue?.mfFile) {
            resultValue.mfFile.content = mfFileData;
          }
          break;
        case 400:
        case 404:
          error = t('not_found_resource', {
            resource: t('resource_invoice'),
          });
          break;
        case 403:
          error = t('forbidden_resource', {
            resource: t('resource_invoice'),
          });
          break;
        default:
          if (status >= 500) {
            error = t('system_error_input');
          }
          break;
      }
      return {
        data: resultValue,
        error,
      };
    }
  }, [
    searchableId,
    isLoading,
    isFetching,
    mfFileIsFetching,
    isRefetching,
    error,
    mfFileError,
    t,
    data?.status,
    data?.data,
    operationStatusToErrorMessage,
    mfFileData,
  ]);
};

export const useReceivedInvoiceSearch: FetchReceivedInvoiceHooks = (
  invoiceId: string
) => {
  const { data, error, refetch } = useGetReceivedInvoiceSuspense(invoiceId);
  if (error) throw error;
  const body = data.data;
  if (data.status !== 200) {
    if (isCommonError(body)) {
      throw new Error(body.messages?.join('\n'));
    } else {
      throw new Error(data.statusText);
    }
  }
  return {
    data: data.data,
    refetch,
  };
};

type TRequestReceivedInvoiceTransactionOcrMutateAsync = ReturnType<
  typeof useRequestReceivedInvoiceTransactionOcr
>['mutateAsync'];
type RequestReceivedInvoiceTransactionOcrMutateArgs =
  Parameters<TRequestReceivedInvoiceTransactionOcrMutateAsync>['0'];

export const useReceivedInvoiceTransactionOcr = () => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } = useRequestReceivedInvoiceTransactionOcr(
    {
      mutation: {
        onSuccess: async (_data, { id }) => {
          await Promise.all([
            query.invalidateQueries({
              queryKey: getGetReceivedInvoiceQueryKey(id),
            }),
          ]);
        },
      },
    }
  );
  const mutateAsync = useCallback(
    async (variable: RequestReceivedInvoiceTransactionOcrMutateArgs) => {
      let resp: AxiosResponse<void | RequestReceivedInvoiceTransactionOcrMutationError>;
      try {
        resp = await _mutateAsync(variable);
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }
      if (isSuccess(resp.status)) return { isSuccessful: true } as const;

      if (!resp.data || !isCommonError(resp.data)) {
        throw new Error(
          `Unexpected transaction OCR response shape: ${resp.status}`
        );
      }

      const { code, messages } = resp.data;

      return {
        isSuccessful: false,
        code: code ?? '',
        messages: messages ?? [],
      } as const;
    },
    [_mutateAsync]
  );
  return { mutateAsync };
};

type ReceivedInvoiceTransactionOcrCsvArgs = {
  id: string;
  csvEncode: CsvEncodeParameter;
};

export const useReceivedInvoiceTransactionOcrCsv = () => {
  const getCsv = useGetReceivedInvoiceTransactionOcrCsvHook();

  return useCallback(
    async ({ id, csvEncode }: ReceivedInvoiceTransactionOcrCsvArgs) => {
      let response: AxiosResponse<Blob>;
      try {
        response = await getCsv(id, { csv_encode: csvEncode });
      } catch (error) {
        if (!isAxiosError<Blob>(error) || !error.response) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        response = error.response;
      }

      if (isSuccess(response.status)) {
        return {
          isSuccessful: true,
          blob: response.data,
          fileName: parseFileName(response.headers, 'transaction_ocr.csv'),
        } as const;
      }

      const commonError = await extractCommonErrorFromBlob(response.data);
      if (!commonError) {
        throw new Error(
          `Unexpected transaction OCR CSV response shape: ${response.status}`
        );
      }

      const { code, messages } = commonError;

      return {
        isSuccessful: false,
        code: code ?? '',
        messages: messages ?? [],
      } as const;
    },
    [getCsv]
  );
};

type TArchiveReceivedInvoiceMutateAsync = ReturnType<
  typeof useArchiveReceivedInvoice
>['mutateAsync'];
type ArchiveReceivedInvoiceMutateArgs =
  Parameters<TArchiveReceivedInvoiceMutateAsync>['0'];

export const useReceivedInvoiceArchive = (id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync, error } = useArchiveReceivedInvoice({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          query.invalidateQueries({
            queryKey: getGetReceivedInvoiceQueryKey(id),
          }),
          query.invalidateQueries({
            queryKey: getSearchReceivedInvoicesQueryKey({}),
          }),
        ]);
      },
    },
  });
  const mutateAsync = useCallback(
    async (variable: ArchiveReceivedInvoiceMutateArgs) => {
      const resp = await _mutateAsync(variable);
      if (!isSuccess(resp.status)) throw error;
    },
    [_mutateAsync, error]
  );
  return { mutateAsync };
};

// BE (ap_web#4598) returns an identical, uncoded 403 for "setting disabled"
// and "no longer has access" (e.g. observer, no PIC access) alike, so
// hooks.ts's error handler can't branch on the response itself. It instead
// re-checks this invoice's own detail query — disabled by default, fired on
// demand via `refetch` — to tell a still-viewable 403 (setting disabled:
// keep the drawer open) from a truly-lost one (close the drawer instead).
export const useReceivedInvoiceViewPermissionCheck = (id: string) => {
  const headers = useAgentApplicantResourceHeaders('received_invoices');
  const { refetch } = useGetReceivedInvoice(id, {
    query: { enabled: false, queryKey: getGetReceivedInvoiceQueryKey(id) },
    request: { headers },
  });
  return refetch;
};

// Lets hooks.ts refresh the received-invoices list after a 403 without
// importing ap-openapi itself (restricted to this services layer).
export const useInvalidateReceivedInvoicesList = () => {
  const query = useQueryClient();
  return useCallback(
    () =>
      query.invalidateQueries({
        queryKey: getSearchReceivedInvoicesQueryKey({}),
      }),
    [query]
  );
};

// Shared by every 突合サービス連携 action (NA-5445/NA-5806/NA-5526) — they're
// all just a status transition on the same resource, with identical
// invalidation/error-normalization behavior. Each ticket's public hook below
// is a thin wrapper fixing the target `status`.
const useReceivedInvoiceReconciliationUpdate = (id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    useUpdateReceivedInvoiceReconciliation();

  return useCallback(
    async (status: ReceivedInvoiceReconciliationStatus) => {
      try {
        const resp = await _mutateAsync({ id, data: { status } });

        // Orval's `onSuccess` fires whenever the mutation promise resolves,
        // not on an actual 2xx — the underlying axios client doesn't throw
        // for 4xx (only >=500). So invalidation must live here, gated on
        // the real status, or a 403 refetches this invoice's detail query
        // and surfaces a second, unrelated error toast via
        // SuspenseErrorBoundary (Details.tsx throws on a non-200 body).
        if (isSuccess(resp.status)) {
          await Promise.all([
            query.invalidateQueries({
              queryKey: getGetReceivedInvoiceQueryKey(id),
            }),
            query.invalidateQueries({
              queryKey: getSearchReceivedInvoicesQueryKey({}),
            }),
          ]);
          return { status: 'success' as const };
        }

        return {
          status: 'error' as const,
          errors: normalizeResponseError(resp.status, resp.data),
        };
      } catch (error) {
        return {
          status: 'error' as const,
          errors: normalizeThrownError(error),
        };
      }
    },
    [_mutateAsync, id, query]
  );
};

export const useReceivedInvoiceReconciliationSend = (id: string) => {
  const updateReconciliation = useReceivedInvoiceReconciliationUpdate(id);
  const mutateAsync = useCallback(
    () => updateReconciliation('waiting'),
    [updateReconciliation]
  );
  return { mutateAsync };
};

export const useReceivedInvoiceReconciliationExclude = (id: string) => {
  const updateReconciliation = useReceivedInvoiceReconciliationUpdate(id);
  const mutateAsync = useCallback(
    () => updateReconciliation('excluded'),
    [updateReconciliation]
  );
  return { mutateAsync };
};

export const useReceivedInvoiceReconciliationCancelExclusion = (id: string) => {
  const updateReconciliation = useReceivedInvoiceReconciliationUpdate(id);
  const mutateAsync = useCallback(
    () => updateReconciliation('unset'),
    [updateReconciliation]
  );
  return { mutateAsync };
};

type TRestoreReceivedInvoiceMutateAsync = ReturnType<
  typeof useRestoreReceivedInvoice
>['mutateAsync'];
type RestoreReceivedInvoiceMutateArgs =
  Parameters<TRestoreReceivedInvoiceMutateAsync>['0'];
type RestoreReceivedInvoiceMutateOption =
  Parameters<TRestoreReceivedInvoiceMutateAsync>['1'] & TFetchQueries;

export const useReceivedInvoiceRestoreArchive = (invoiceId: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync, error } = useRestoreReceivedInvoice({
    mutation: {
      onSuccess: () => {
        query.invalidateQueries({
          queryKey: getGetReceivedInvoiceQueryKey(invoiceId),
        });
      },
    },
  });
  const refetchQueries = useRefetchQueries();
  const mutateAsync = useCallback(
    async (
      variable: RestoreReceivedInvoiceMutateArgs,
      options?: RestoreReceivedInvoiceMutateOption
    ) => {
      const resp = await _mutateAsync(variable, options);
      if (!isSuccess(resp.status)) throw error;
      return refetchQueries(options?.refetchQueries);
    },
    [_mutateAsync, error, refetchQueries]
  );
  return { mutateAsync };
};

type TUpdateReceivedInvoiceMutateAsync = ReturnType<
  typeof useBaseUpdateReceivedInvoice
>['mutateAsync'];
type UpdateReceivedInvoiceMutateArgs =
  Parameters<TUpdateReceivedInvoiceMutateAsync>['0'];
type UpdateReceivedInvoiceSuccess = {
  status: 'success';
};
type UpdateReceivedInvoiceError = {
  status: 'invalid';
  errors: UpdateReceivedInvoiceErrorResponse;
};
type UpdateReceivedInvoiceCommonError = {
  status: 'error';
  errors: CommonError;
};
type UpdateReceivedInvoiceResult =
  | UpdateReceivedInvoiceSuccess
  | UpdateReceivedInvoiceError
  | UpdateReceivedInvoiceCommonError;
export const useUpdateReceivedInvoice = (id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } = useBaseUpdateReceivedInvoice({
    mutation: {
      onSuccess: async () =>
        await Promise.all([
          query.invalidateQueries({
            queryKey: getGetReceivedInvoiceQueryKey(id),
          }),
          query.invalidateQueries({
            queryKey: getSearchReceivedInvoicesQueryKey({}),
          }),
        ]),
    },
  });
  const mutateAsync = useCallback(
    async (
      args: UpdateReceivedInvoiceMutateArgs
    ): Promise<UpdateReceivedInvoiceResult> => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
        };
      } else {
        if (isCommonError(resp.data)) {
          return {
            status: 'error',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (resp.data ?? {}) as CommonError,
          };
        } else {
          return {
            status: 'invalid',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (resp.data ?? {}) as UpdateReceivedInvoiceErrorResponse,
          };
        }
      }
    },
    [_mutateAsync]
  );
  return { mutateAsync };
};

const dummyBlob = new Blob([]);
export const useGetXMLBlob = (invoiceId: string = '') => {
  const { data, error } = useGetSignedXmlFileUrlSuspense(invoiceId);
  if (error) {
    throw error;
  }
  const signedUrl = useMemo(() => data.data.signed_url, [data.data.signed_url]);
  const { data: s3Data, error: s3Error } =
    useDownloadS3SignedUrlSuspense(signedUrl);
  if (s3Error) {
    throw error;
  }
  const downloadBlob = useMemo(() => s3Data.data || dummyBlob, [s3Data.data]);
  return { downloadBlob };
};

type CreateReceivedInvoiceManualSuccess = {
  status: 'success';
  results: BulkUploadReceivedInvoicesResult[];
};
type CreateReceivedInvoiceManualCommonError = {
  status: 'error';
  errors: CommonError;
};
type CreateReceivedInvoiceManualInvalidError = {
  status: 'invalid';
  errors: BulkUploadReceivedInvoicesErrorResponse;
};
type CreateReceivedInvoiceManualResult =
  | CreateReceivedInvoiceManualSuccess
  | CreateReceivedInvoiceManualCommonError
  | CreateReceivedInvoiceManualInvalidError;
export const useReceivedInvoiceBulkManualUpload = () => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } = useBulkUploadReceivedInvoices({
    mutation: {
      onSuccess: () => {
        query.invalidateQueries({
          queryKey: getSearchReceivedInvoicesQueryKey({}),
        });
      },
    },
  });
  const mutateAsync = useCallback(
    async (
      args: BulkUploadReceivedInvoicesRequest
    ): Promise<CreateReceivedInvoiceManualResult> => {
      const resp = await _mutateAsync({ data: args });
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
          results: resp.data?.results ?? [],
        };
      } else {
        if (isCommonError(resp.data)) {
          return {
            status: 'error',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (resp.data ?? {}) as CommonError,
          };
        } else {
          return {
            status: 'invalid',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (resp.data ??
              {}) as BulkUploadReceivedInvoicesErrorResponse,
          };
        }
      }
    },
    [_mutateAsync]
  );
  return { mutateAsync };
};
